import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { getPaymentMode } from '../../utils/helpers';

// Helper function to get branch name
const getBranchName = (branchId, branchesData) => {
if (!branchesData || !Array.isArray(branchesData)) return 'Unknown';
const branch = branchesData.find(b => parseInt(b.id) === parseInt(branchId));
return branch ? branch.name : 'Unknown';
};

// Helper function to get room type name
const getRoomTypeName = (roomTypeId, roomTypesData) => {
if (!roomTypesData || !Array.isArray(roomTypesData)) return 'Unknown';
const roomType = roomTypesData.find(r => parseInt(r.id) === parseInt(roomTypeId));
return roomType ? roomType.roomtype : 'Unknown';
};

// Helper function to get room unit name
const getRoomUnitName = (roomUnitId, roomUnitsData) => {
if (!roomUnitsData || !Array.isArray(roomUnitsData)) return 'Unknown';
const roomUnit = roomUnitsData.find(u => parseInt(u.id) === parseInt(roomUnitId));
return roomUnit ? roomUnit.name : 'Unknown';
};

// Helper function to get customer name
const getCustomerName = (customerId, customerData) => {
if (!customerData || !Array.isArray(customerData)) return 'Unknown';
const customer = customerData.find(c => parseInt(c.id) === parseInt(customerId));
return customer ? customer.customer_name : 'Unknown';
};

// Helper function to get status name
const getStatusName = (status) => {
switch(parseInt(status)) {
    case 1: return 'Active';
    case 0: return 'Inactive';
    default: return 'Unknown';
}
};

// Helper function to format currency
const formatCurrency = (amount) => {
if (!amount && amount !== 0) return '0';
return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(amount)  + " "+"sh";
};

// Helper function to format date
const formatDate = (dateString) => {
if (!dateString) return 'N/A';
return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

// Helper function to split text into multiple lines for PDF
const splitTextIntoLines = (doc, text, maxWidth) => {
if (!text) return [''];
const words = text.split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for Guest House Bookings PDF (Landscape mode)
const drawGuestBookingsTable = (doc, data, startY, pageWidth) => {
const margin = 14;
const rowHeight = 12;

// Column widths: #, Date, Customer, Cost, Qty, Total, Paid, Balance, Status, Branch
const colWidths = [10, 25, 40, 20, 15, 25, 25, 25, 20, 25];

let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;

if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('l');
    currentY = margin;
}

// Draw table header
doc.setFillColor(255, 255, 255);
doc.setTextColor(25, 118, 210);
doc.setFont(undefined, 'bold');

const headers = ['#', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Status', 'Branch'];
let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

// Draw header underline
doc.setDrawColor(25, 118, 210);
doc.setLineWidth(0.5);
doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('l');
    currentY = margin;
    
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    doc.setDrawColor(25, 118, 210);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Export Guest House Bookings to Excel - Show all attributes
export const exportToExcel = (bookings, branchesData = [], roomTypesData = [], roomUnitsData = [], customerData = [], filters = {}) => {
try {
    // Ensure bookings is an array
    if (!Array.isArray(bookings)) {
    throw new Error('Guest house bookings data is not an array');
    }

    // Prepare data for Excel with all attributes
    const excelData = bookings.map((booking, index) => {
    const safeBooking = booking || {};
    
    return {
        'No.': index + 1,
        'ID': safeBooking.id || '',
        'Booking Number': safeBooking.booking_no || '',
        'Date': formatDate(safeBooking.created_at),
        'Customer ID': safeBooking.customer_id || '',
        'Customer Name': getCustomerName(safeBooking.customer_id, customerData),
        'Customer Phone': safeBooking.customer_phone || '',
        'Customer Email': safeBooking.customer_email || '',
        'Customer Address': safeBooking.customer_address || '',
        'Room Type ID': safeBooking.room_type_id || '',
        'Room Type': getRoomTypeName(safeBooking.room_type_id, roomTypesData),
        'Room Unit ID': safeBooking.room_unit_id || '',
        'Room Unit': getRoomUnitName(safeBooking.room_unit_id, roomUnitsData),
        'Room Name': safeBooking.room_name || '',
        'Cost per Unit': formatCurrency(safeBooking.cost || 0),
        'Quantity': safeBooking.quantity || 1,
        'Total Amount': formatCurrency(safeBooking.total_amount || 0),
        'Paid Amount': formatCurrency(safeBooking.paid_amount || 0),
        'Balance Amount': formatCurrency(safeBooking.balance_amount || 0),
        'Payment Method': getPaymentMode(safeBooking.payment_method),
        'Branch ID': safeBooking.branch_id || '',
        'Branch': getBranchName(safeBooking.branch_id, branchesData),
        'Status': getStatusName(safeBooking.status),
        'Check-in Date': safeBooking.check_in_date || '',
        'Check-out Date': safeBooking.check_out_date || '',
        'Description': safeBooking.description || '',
        'Created Date': formatDate(safeBooking.created_at),
        'Updated Date': formatDate(safeBooking.updated_at)
    };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Guest House Bookings');

    // Generate Excel file and download
    const fileName = `guest_house_bookings_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Guest house bookings Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Guest House Bookings - Show only selected columns in Landscape mode
export const exportToPDF = (bookings, branchesData = [], roomTypesData = [], roomUnitsData = [], customerData = [], filters = {}) => {
try {
    // Ensure bookings is an array
    if (!Array.isArray(bookings)) {
    throw new Error('Guest house bookings data is not an array');
    }

    // Create PDF in landscape mode
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Set document properties
    doc.setProperties({
    title: 'Guest House Bookings Report',
    subject: 'Guest House Bookings Export',
    author: companyInfo.name,
    keywords: 'guest house, bookings, export, report',
    creator: companyInfo.name
    });
    
    // Get landscape page width and height
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('GUEST HOUSE BOOKINGS REPORT', pageWidth / 2, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Bookings: ${bookings.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters && (filters.customer_id || filters.room_type_id || filters.room_unit_id || 
        filters.payment_method || filters.status || filters.dateFrom || filters.dateTo)) {
    doc.text('Applied Filters:', 14, currentY);
    currentY += 6;
    
    if (filters.customer_id) {
        doc.text(`• Customer: ${getCustomerName(filters.customer_id, customerData)}`, 20, currentY);
        currentY += 6;
    }
    if (filters.room_type_id) {
        doc.text(`• Room Type: ${getRoomTypeName(filters.room_type_id, roomTypesData)}`, 20, currentY);
        currentY += 6;
    }
    if (filters.room_unit_id) {
        doc.text(`• Room Unit: ${getRoomUnitName(filters.room_unit_id, roomUnitsData)}`, 20, currentY);
        currentY += 6;
    }
    if (filters.payment_method) {
        doc.text(`• Payment Method: ${getPaymentMode(filters.payment_method)}`, 20, currentY);
        currentY += 6;
    }
    if (filters.status) {
        doc.text(`• Status: ${getStatusName(filters.status)}`, 20, currentY);
        currentY += 6;
    }
    if (filters.dateFrom) {
        doc.text(`• From date: ${formatDate(filters.dateFrom)}`, 20, currentY);
        currentY += 6;
    }
    if (filters.dateTo) {
        doc.text(`• To date: ${formatDate(filters.dateTo)}`, 20, currentY);
        currentY += 6;
    }
    currentY += 6;
    }
    
    // Prepare table data with only: #, Date, Customer, Cost, Qty, Total, Paid, Balance, Status, Branch
    const tableData = bookings.map((booking, index) => {
    const safeBooking = booking || {};
    return [
        (index + 1).toString(),
        formatDate(safeBooking.created_at),
        getCustomerName(safeBooking.customer_id, customerData),
        formatCurrency(safeBooking.cost || 0),
        safeBooking.quantity || 1,
        formatCurrency(safeBooking.total_amount || 0),
        formatCurrency(safeBooking.paid_amount || 0),
        formatCurrency(safeBooking.balance_amount || 0),
        getStatusName(safeBooking.status),
        getBranchName(safeBooking.branch_id, branchesData)
    ];
    });
    
    // Draw the table
    drawGuestBookingsTable(doc, tableData, currentY + 5, pageWidth);
    
    const fileName = `guest_house_bookings_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Guest house bookings PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// CSV export for Guest House Bookings - Show all attributes
export const exportToCSV = (bookings, branchesData = [], roomTypesData = [], roomUnitsData = [], customerData = []) => {
try {
    // Ensure bookings is an array
    if (!Array.isArray(bookings)) {
    throw new Error('Guest house bookings data is not an array');
    }

    const headers = [
    'ID',
    'Booking Number',
    'Date',
    'Customer ID',
    'Customer Name',
    'Customer Phone',
    'Customer Email',
    'Customer Address',
    'Room Type ID',
    'Room Type',
    'Room Unit ID',
    'Room Unit',
    'Room Name',
    'Cost per Unit',
    'Quantity',
    'Total Amount',
    'Paid Amount',
    'Balance Amount',
    'Payment Method',
    'Branch ID',
    'Branch',
    'Status',
    'Check-in Date',
    'Check-out Date',
    'Description',
    'Created Date',
    'Updated Date'
    ];
    
    const csvData = bookings.map(booking => {
    const safeBooking = booking || {};
    
    return [
        safeBooking.id || '',
        safeBooking.booking_no || '',
        formatDate(safeBooking.created_at),
        safeBooking.customer_id || '',
        `"${getCustomerName(safeBooking.customer_id, customerData).replace(/"/g, '""')}"`,
        `"${(safeBooking.customer_phone || '').replace(/"/g, '""')}"`,
        `"${(safeBooking.customer_email || '').replace(/"/g, '""')}"`,
        `"${(safeBooking.customer_address || '').replace(/"/g, '""')}"`,
        safeBooking.room_type_id || '',
        `"${getRoomTypeName(safeBooking.room_type_id, roomTypesData).replace(/"/g, '""')}"`,
        safeBooking.room_unit_id || '',
        `"${getRoomUnitName(safeBooking.room_unit_id, roomUnitsData).replace(/"/g, '""')}"`,
        `"${(safeBooking.room_name || '').replace(/"/g, '""')}"`,
        safeBooking.cost || 0,
        safeBooking.quantity || 1,
        safeBooking.total_amount || 0,
        safeBooking.paid_amount || 0,
        safeBooking.balance_amount || 0,
        `"${getPaymentMode(safeBooking.payment_method).replace(/"/g, '""')}"`,
        safeBooking.branch_id || '',
        `"${getBranchName(safeBooking.branch_id, branchesData).replace(/"/g, '""')}"`,
        `"${getStatusName(safeBooking.status).replace(/"/g, '""')}"`,
        `"${(safeBooking.check_in_date || '').replace(/"/g, '""')}"`,
        `"${(safeBooking.check_out_date || '').replace(/"/g, '""')}"`,
        `"${(safeBooking.description || '').replace(/"/g, '""')}"`,
        formatDate(safeBooking.created_at),
        formatDate(safeBooking.updated_at)
    ];
    });

    const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `guest_house_bookings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Guest house bookings CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};